import { type TFunction } from 'i18next'
import * as R from 'remeda'

import {
  type FiveSegment,
  fiveSegmentLabelI18nKeyMap,
  fiveSegments,
  formatNineSegment,
  type NineSegment,
  nineSegments,
} from '~/utils'

import { type FiveSegmentsAnswerData } from './buildFiveSegmentsAnswersArrayData'
import { type NineSegmentsAnswerData } from './buildNineSegmentsAnswersArrayData'
import { type FiveSegmentColumnKey } from './fiveSegments'
import { type NineSegmentColumnKey } from './nineSegments'

// 顧客データ画面のAIコメント（場合分けA = 通常設問）用に、セグメントごとの %Top5 を
// SPA 側で確定させて表テキスト末尾に追記する。appendDriverTop5Block と同じ
// append-only の凍結フォーマット契約。
//
// WHY: 場合分けAでは列名が一意（番号付き 8.消極 等）なので列の取り違えは起きにくいが、
// 「20数行から%上位5件を選ぶ」作業自体が壊れる — 最大値（あてはまるものはない 31.9% 等）を
// 読み飛ばす・下位の項目を採用する事故が実データで繰り返し観測された。選択をコードで確定し、
// LLM の仕事を書き写しに落とす（場合分けBのドライバーTop5と同じ解法）。
//
// ドライバーTop5と違い、このブロックは設問種別でゲートしない（全設問に付ける）:
// %の事実の列挙であり、通常設問を場合分けBへ誤誘導する内容を含まない。イメージ設問
// （場合分けB）ではプロンプトがドライバーTop5ブロックだけを指名するので使われない。
//
// 行順は表の列順（左から右 = 未認知 → … → 積極ロイヤル）に固定し、行番号で
// 出力行との位置対応を固定する。全体列は載せない（既存出力の慣行に合わせ、セグメント
// 9行/5行のみ）。非表示にされた列はその行ごと落とす（表に無い列を LLM が書き写さない
// ようにするため。落として詰め直しても、場合分けAの出力は「ブロックの行数分」繰り返す
// 契約なので対応は崩れない）。
//
// 顧客属性の行 (ライフステージ等) も設問行と同じ形の節にする（節見出しは設問名の代わりに
// 属性グループ名）。ブロック対象外だった間、属性のみの表でフォールバックに落ち、
// 一部セグメントの項目欠落・形式ゆれが実データで観測されたため。表の行順（設問 → 顧客属性）
// に合わせ、属性の節は設問の節の後ろに置く。
export const SEGMENT_TOP5_BLOCK_HEADING =
  '# セグメント別Top5 / Per-segment Top5（各セグメント列の%上位5件を算出済み。この値だけを使い、表から選び直さない）'

// 0% を超える項目が無いセグメントの行。行番号と出力行の位置対応を保つため行ごと省略はしない。
export const SEGMENT_TOP5_NONE_MARK = '（該当なし / none）'

const TOP_N = 5

const formatPercent = (ratio: number): string => `${(ratio * 100).toFixed(1)}%`

type SegmentLine = {
  label: string
  entries: { text: string; ratio: number }[]
}

const renderLine = (index: number, line: SegmentLine): string => {
  const top = line.entries
    .filter((e) => Number.isFinite(e.ratio) && 0 < e.ratio)
    .sort((a, b) => b.ratio - a.ratio)
    .slice(0, TOP_N)
  const body =
    top.length === 0
      ? SEGMENT_TOP5_NONE_MARK
      : top.map((e) => `${e.text} ${formatPercent(e.ratio)}`).join(' / ')

  return `${index + 1}. ${line.label}: ${body}`
}

const renderBlock = (
  questions: { questionText: string; lines: SegmentLine[] }[],
): string | null => {
  // 全列を非表示にすると lines が空になる。見出しだけのブロックを出さない。
  const sections = questions.filter((q) => 0 < q.lines.length)
  if (sections.length === 0) {
    return null
  }

  return [
    SEGMENT_TOP5_BLOCK_HEADING,
    ...sections.flatMap((q) => [
      `## ${q.questionText}`,
      ...q.lines.map((line, i) => renderLine(i, line)),
    ]),
  ].join('\n')
}

type NineSegSourceRow = {
  answerOption: { text: string }
  question: { question_text: string }
  ratios: Pick<NineSegmentsAnswerData['ratios'], NineSegment>
}

// 顧客属性の行。項目名は customerAttribute.name（表の行ラベルと同じ文字列）、
// 節見出しは customerAttributeGroup.name。
type NineSegCustomerAttributeSourceRow = {
  customerAttribute: { name: string }
  customerAttributeGroup: { name: string }
  ratios: Pick<NineSegmentsAnswerData['ratios'], NineSegment>
}

/**
 * 9segs のセグメント別 %Top5 ブロックを表テキスト末尾に追記します。
 *
 * 行は表の列順の 9 行固定: 1. 未認知 / 2. 消極 認知未購入 / 3. 積極 認知未購入 /
 * … / 9. 積極ロイヤル。各行は 0% を超える項目のみを降順で最大 5 件。
 *
 * 非表示にされたセグメント列は行ごと落とす。場合分けAの出力は「セグメント数分
 * （ブロックの行数分）繰り返す」契約で、行番号は並び順を固定するためのものなので、
 * 落として詰め直しても出力フォーマットとの対応は崩れない。
 *
 * @param serialized - シリアライズ済みの表テキスト (他のブロックが付いていてもよい)。
 * @param answersArrayData - 表示中の回答行 (filterVisibleAnswersArrayData 適用済み)。
 * @param t - 行ラベル用の翻訳関数 (表と同じ UI 言語)。
 * @param hiddenColumnKeys - 非表示にされた列。ウィザードは常に非表示なしなので既定は空。
 * @param customerAttributesArrayData - 表示中の顧客属性行
 *   (filterVisibleCustomerAttributesArrayData 適用済み)。属性グループごとの節になる。
 * @returns ブロックを追記したテキスト。行が無ければ入力をそのまま返す。
 */
export const appendNineSegSegmentTop5Block = (
  serialized: string,
  answersArrayData: readonly (readonly NineSegSourceRow[])[],
  t: TFunction,
  hiddenColumnKeys: readonly NineSegmentColumnKey[] = [],
  customerAttributesArrayData: readonly (readonly NineSegCustomerAttributeSourceRow[])[] = [],
): string => {
  // 表の列順 = nineSegment9(未認知) → nineSegment1(積極ロイヤル)。
  // ラベルはサンプル数しきいブロックと同じ formatNineSegment('%t1') を使い、表側の
  // 見出し語彙（積極 認知未購入 等）と揃える。
  const orderedSegments = R.reverse(nineSegments).filter(
    (segment) => !hiddenColumnKeys.includes(segment),
  )

  const toLines = (
    entriesOf: (segment: NineSegment) => { text: string; ratio: number }[],
  ): SegmentLine[] =>
    orderedSegments.map((segment) => ({
      label: formatNineSegment(segment, '%t1', t).replace(/\s*\n\s*/g, ' '),
      entries: entriesOf(segment),
    }))

  const questions = answersArrayData
    .filter((rows) => 0 < rows.length)
    .map((rows) => ({
      questionText: rows[0].question.question_text,
      lines: toLines((segment) =>
        rows.map((row) => ({
          text: row.answerOption.text,
          ratio: row.ratios[segment],
        })),
      ),
    }))

  const customerAttributeGroups = customerAttributesArrayData
    .filter((rows) => 0 < rows.length)
    .map((rows) => ({
      questionText: rows[0].customerAttributeGroup.name,
      lines: toLines((segment) =>
        rows.map((row) => ({
          text: row.customerAttribute.name,
          ratio: row.ratios[segment],
        })),
      ),
    }))

  const block = renderBlock([...questions, ...customerAttributeGroups])

  return block === null ? serialized : `${serialized}\n\n${block}`
}

type FiveSegSourceRow = {
  answerOption: { text: string }
  question: { question_text: string }
  ratios: Pick<FiveSegmentsAnswerData['ratios'], FiveSegment>
}

type FiveSegCustomerAttributeSourceRow = {
  customerAttribute: { name: string }
  customerAttributeGroup: { name: string }
  ratios: Pick<FiveSegmentsAnswerData['ratios'], FiveSegment>
}

/**
 * 5segs のセグメント別 %Top5 ブロックを表テキスト末尾に追記します。
 *
 * 行は表の列順の 5 行固定: 1. 未認知 / 2. 認知未購入 / 3. 離反 / 4. 一般 / 5. ロイヤル。
 *
 * 非表示にされたセグメント列は行ごと落とす (9segs 版と同じ理由)。
 *
 * @param serialized - シリアライズ済みの表テキスト。
 * @param answersArrayData - 表示中の回答行 (filterVisibleAnswersArrayData 適用済み)。
 * @param t - 行ラベル用の翻訳関数 (表と同じ UI 言語)。
 * @param hiddenColumnKeys - 非表示にされた列。ウィザードは常に非表示なしなので既定は空。
 * @param customerAttributesArrayData - 表示中の顧客属性行
 *   (filterVisibleCustomerAttributesArrayData 適用済み)。属性グループごとの節になる。
 * @returns ブロックを追記したテキスト。行が無ければ入力をそのまま返す。
 */
export const appendFiveSegSegmentTop5Block = (
  serialized: string,
  answersArrayData: readonly (readonly FiveSegSourceRow[])[],
  t: TFunction,
  hiddenColumnKeys: readonly FiveSegmentColumnKey[] = [],
  customerAttributesArrayData: readonly (readonly FiveSegCustomerAttributeSourceRow[])[] = [],
): string => {
  // 表の列順 = fiveSegment5(未認知) → fiveSegment1(ロイヤル)。
  const orderedSegments = R.reverse(fiveSegments).filter(
    (segment) => !hiddenColumnKeys.includes(segment),
  )

  const toLines = (
    entriesOf: (segment: FiveSegment) => { text: string; ratio: number }[],
  ): SegmentLine[] =>
    orderedSegments.map((segment) => ({
      label: t(fiveSegmentLabelI18nKeyMap[segment]),
      entries: entriesOf(segment),
    }))

  const questions = answersArrayData
    .filter((rows) => 0 < rows.length)
    .map((rows) => ({
      questionText: rows[0].question.question_text,
      lines: toLines((segment) =>
        rows.map((row) => ({
          text: row.answerOption.text,
          ratio: row.ratios[segment],
        })),
      ),
    }))

  const customerAttributeGroups = customerAttributesArrayData
    .filter((rows) => 0 < rows.length)
    .map((rows) => ({
      questionText: rows[0].customerAttributeGroup.name,
      lines: toLines((segment) =>
        rows.map((row) => ({
          text: row.customerAttribute.name,
          ratio: row.ratios[segment],
        })),
      ),
    }))

  const block = renderBlock([...questions, ...customerAttributeGroups])

  return block === null ? serialized : `${serialized}\n\n${block}`
}
